import { translate } from "app/language/service"
import { getFormatedNumber } from "app/tools/number"
import { CAMPAIGN_AUCTION_TYPES, SSP_DEAL_AUCTION_TYPES } from "auction/model"
import { isStatusRunningOrRunned } from "campaign/tools/common"
import { getAvailableCurrencyIds } from "currency/tools/common"
import { FieldCheckbox } from "form/components/fields/FieldCheckbox"
import { IForm } from "form/model"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { addImportant } from "ui/tools/common"
import { isPhoneScreen } from "ui/tools/responsive"
import { FieldCheckboxes } from "../../fields/FieldCheckboxes"
import { FieldMoney } from "../../fields/FieldMoney"
import { FieldSelect } from "../../fields/FieldSelect"
import { OrderEditField } from "./OrderEditField"

interface IConfigurationBudgetFormProps {
  form: IForm
  readOnly?: boolean
}

export const ConfigurationBudgetForm = React.memo(({ form, readOnly }: IConfigurationBudgetFormProps): JSX.Element => {
  const {
    context,
    configuration: { budget, dspDeal }
  } = form
  const budgetErrors = context.configuration.errors.budget
  const dspDealErrors = context.configuration.errors.dspDeal
  const isDspDealForm = context.isDspDeal
  return (
    <FlexBox
      vertical
      ai="start"
      jc="start"
      gap
      fullWidth
      sx={{
        "height": "100%",
        "minHeight": "100%",
        "maxHeight": "100%",
        "p": "1rem",
        "& > *": {
          width: "15rem !important"
        }
      }}>
      <FieldSelect
        fieldName="budget.currency_id"
        label={translate("label.currency")}
        value={budget.currency_id}
        options={getAvailableCurrencyIds()}
        optionLabelFn={(currency: string) => currency}
        error={budgetErrors?.currency_id}
        disabled={isStatusRunningOrRunned(form.statusId)}
      />
      {isDspDealForm && (
        <FieldSelect
          fieldName="dspDeal.auction_type"
          label={translate("label.auction")}
          value={dspDeal.auction_type}
          options={SSP_DEAL_AUCTION_TYPES}
          optionLabelFn={(a: number) => translate(`label.auction.${a}`)}
          error={dspDealErrors?.auction_type}
          disabled={isStatusRunningOrRunned(form.statusId)}
        />
      )}
      {!isDspDealForm && (
        <FlexBox jc="start" wrap gap sx={{ width: addImportant("100%") }}>
          <FieldMoney
            currencyId={budget.currency_id}
            fieldName="budget.budget"
            label={translate("label.budget")}
            value={budget.budget}
            error={budgetErrors?.budget}
            placeholder={`ex: ${getFormatedNumber(100000)}`}
            disabled={readOnly}
            sx={{ width: addImportant("15rem"), minWidth: "15rem", maxWidth: "15rem" }}
            required
            clearable={!readOnly}
          />
          {!isDspDealForm && !form.context.isForecast && (
            <OrderEditField
              form={form}
              toDisplayInfo="budget"
              budgetForm={budget}
              sx={{ maxWidth: isPhoneScreen() ? "100%" : addImportant("calc(100% - 16rem)") }}
            />
          )}
        </FlexBox>
      )}
      {!isDspDealForm && (
        <FieldMoney
          currencyId={budget.currency_id}
          fieldName="budget.max_daily_spend"
          label={translate("label.daily.max.spend")}
          value={budget.max_daily_spend}
          placeholder={`ex: ${getFormatedNumber(100000)}`}
          error={budgetErrors?.max_daily_spend}
          disabled={readOnly}
        />
      )}
      {!isDspDealForm ? (
        <>
          <FieldCheckbox
            fieldName="budget.equalizeDailyMaxSpendFromSchedule"
            label={translate("label.equalizeDailyMaxSpendFromSchedule")}
            value={budget.equalizeDailyMaxSpendFromSchedule}
            disabled={readOnly}
          />
          <FieldMoney
            currencyId={budget.currency_id}
            fieldName="budget.max_bid"
            label={translate("label.max.bid")}
            value={budget.max_bid}
            placeholder="ex: 6"
            error={budgetErrors?.max_bid}
            disabled={readOnly}
            required
            sx={{ width: "100% !important" }}
            sxField={{ width: "15rem", minWidth: "15rem", maxWidth: "15rem" }}
            clearable={!readOnly}
          />
          <FieldCheckboxes
            fieldName="budget.auctions"
            label={translate("label.auction")}
            options={CAMPAIGN_AUCTION_TYPES.map(auctionType => ({
              value: auctionType,
              label: translate(`label.auction.${auctionType}`)
            }))}
            value={budget.auctions as number[]}
            error={budgetErrors?.auctions as string}
            disabled={readOnly}
            required
            sx={{ width: "24rem !important" }}
          />
        </>
      ) : (
        <FieldMoney
          currencyId={budget.currency_id}
          fieldName="dspDeal.bid_floor"
          label={translate("label.bid-floor")}
          value={dspDeal.bid_floor}
          placeholder="ex: 6"
          error={dspDealErrors?.bid_floor}
          disabled={readOnly}
          required
          sx={{ width: "100% !important" }}
          sxField={{ width: "15rem", minWidth: "15rem", maxWidth: "15rem" }}
          clearable={!readOnly}
        />
      )}
    </FlexBox>
  )
})
